import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Flame, Battery, Focus, Moon } from "lucide-react";
import Logo from "../components/Logo";

/**
 * Mood Insights — a public, shareable consumer-facing dashboard demo.
 * Companion piece to the nurse-specific AOT (Awareness Over Time) on /ana.
 * Vocabulary is intentionally consumer: Vibe / Energy / Focus / Rest / Calm.
 */

const INSIGHTS_DATA = {
  "3M": {
    labels: ["Wk 1", "Wk 3", "Wk 5", "Wk 7", "Wk 9", "Wk 12"],
    vibe:   [48, 55, 62, 68, 74, 82],
    energy: [55, 60, 65, 70, 74, 80],
    focus:  [45, 52, 60, 66, 72, 78],
    rest:   [50, 58, 64, 70, 75, 84],
  },
  "6M": {
    labels: ["DEC", "JAN", "FEB", "MAR", "APR", "MAY"],
    vibe:   [40, 50, 58, 66, 78, 88],
    energy: [48, 56, 62, 70, 78, 84],
    focus:  [42, 50, 60, 65, 74, 82],
    rest:   [45, 55, 65, 72, 80, 90],
  },
  "12M": {
    labels: ["Jun", "Aug", "Oct", "Dec", "Feb", "Apr"],
    vibe:   [32, 42, 52, 62, 74, 90],
    energy: [38, 46, 54, 64, 72, 82],
    focus:  [36, 44, 54, 62, 70, 80],
    rest:   [40, 50, 58, 68, 76, 88],
  },
};

const MOMENTS = [
  { icon: "🌅", title: "Morning ritual clicked", date: "Apr 22", vibe: "+18", tag: "Energy" },
  { icon: "🧘", title: "First 30-day streak",   date: "Apr 12", vibe: "+24", tag: "Focus" },
  { icon: "🌙", title: "Rest reset week",       date: "Mar 30", vibe: "+15", tag: "Rest" },
  { icon: "🎧", title: "Discovered Deep Blue",  date: "Mar 04", vibe: "+11", tag: "Calm" },
];

const THEMES = [
  { label: "Ground",     value: 68, color: "#8ec4ff" },
  { label: "Flow",       value: 84, color: "#EED89A" },
  { label: "Open",       value: 55, color: "#c5b0ff" },
  { label: "Curious",    value: 47, color: "#7de3c5" },
  { label: "Playful",    value: 62, color: "#f9a8d4" },
];

/* Build a smooth bezier path from 0..100 series */
function smoothPath(values, W, H, pad = 12) {
  const iw = W - pad * 2, ih = H - pad * 2;
  const pts = values.map((v, i) => [pad + (i / (values.length - 1)) * iw, pad + (1 - v / 100) * ih]);
  if (pts.length < 2) return { d: "", pts: [] };
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i]; const [x1, y1] = pts[i + 1];
    const cpx = (x0 + x1) / 2;
    d += ` C ${cpx} ${y0}, ${cpx} ${y1}, ${x1} ${y1}`;
  }
  return { d, pts };
}

/** Circular ring stat */
function Ring({ label, value, color, icon: Icon, unit = "" }) {
  const R = 34, C = 2 * Math.PI * R;
  const offset = C - (value / 100) * C;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-4" data-testid={`insights-ring-${label.toLowerCase()}`}>
      <div className="relative flex-shrink-0">
        <svg width="80" height="80" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r={R} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
          <circle
            cx="40" cy="40" r={R}
            stroke={color} strokeWidth="6" fill="none"
            strokeDasharray={C} strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 40 40)"
            style={{ transition: "stroke-dashoffset 0.9s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon size={16} style={{ color }} />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] uppercase tracking-[0.24em] text-zinc-400">{label}</div>
        <div className="mt-0.5 font-heading text-white text-2xl leading-none">
          {value}<span className="text-zinc-500 text-sm ml-0.5">{unit || "/100"}</span>
        </div>
      </div>
    </div>
  );
}

export default function InsightsPage() {
  const [range, setRange] = useState("6M");
  const [hover, setHover] = useState(null);
  const data = INSIGHTS_DATA[range];

  const W = 640, H = 220;
  const vibe   = useMemo(() => smoothPath(data.vibe,   W, H), [data]);
  const energy = useMemo(() => smoothPath(data.energy, W, H), [data]);
  const focus  = useMemo(() => smoothPath(data.focus,  W, H), [data]);
  const rest   = useMemo(() => smoothPath(data.rest,   W, H), [data]);

  const latest = {
    vibe:   data.vibe[data.vibe.length - 1],
    energy: data.energy[data.energy.length - 1],
    focus:  data.focus[data.focus.length - 1],
    rest:   data.rest[data.rest.length - 1],
  };

  const streak = 32; // demo: consecutive days with a groove

  return (
    <div className="min-h-screen bg-[#03060f] text-white" data-testid="insights-page">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 -left-40 w-[60vw] h-[60vw] rounded-full bg-violet-500/12 blur-[160px]" />
        <div className="absolute -bottom-40 -right-40 w-[55vw] h-[55vw] rounded-full bg-amber-400/10 blur-[160px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-white/5 backdrop-blur-xl bg-[#030407]/60">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-zinc-300 hover:text-white transition text-sm" data-testid="insights-back">
            <ArrowLeft size={16} /> Back
          </Link>
          <Logo size="md" />
          <div className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-amber-300/85">
            <Sparkles size={12} className="text-amber-300" />
            Mood Insights · Demo
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        {/* Hero */}
        <div className="max-w-3xl mb-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-3">Personal insights</div>
          <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance">
            Your vibe <span className="italic font-medium">over time.</span>
          </h1>
          <p className="mt-5 font-body text-zinc-300/90 text-base sm:text-lg leading-relaxed">
            A private, reflective view of how your grooves have shifted your energy, focus, rest,
            and overall vibe across the last few months. Not a diagnosis. A pattern.
          </p>
        </div>

        {/* Top row — Streak + Rings */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,1fr)_2fr] gap-4 mb-6">
          {/* Streak card */}
          <div className="rounded-3xl border border-amber-300/30 bg-gradient-to-br from-amber-300/[0.08] to-transparent p-6 relative overflow-hidden" data-testid="insights-streak">
            <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-amber-300/15 blur-3xl" aria-hidden />
            <div className="relative">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-amber-300 mb-3">
                <Flame size={13} /> Current groove streak
              </div>
              <div className="flex items-baseline gap-2">
                <div className="font-heading text-white text-6xl leading-none">{streak}</div>
                <div className="text-zinc-400 text-lg">days</div>
              </div>
              <div className="mt-3 text-[13px] text-zinc-300/85 leading-relaxed">
                Your longest run yet — this rhythm is where the compounding starts.
              </div>
            </div>
          </div>
          {/* Rings */}
          <div className="grid grid-cols-2 gap-3">
            <Ring label="Energy" value={latest.energy} color="#EED89A" icon={Battery} />
            <Ring label="Focus"  value={latest.focus}  color="#6ea3ff" icon={Focus} />
            <Ring label="Rest"   value={latest.rest}   color="#c5b0ff" icon={Moon} />
            <Ring label="Vibe"   value={latest.vibe}   color="#7de3c5" icon={Sparkles} />
          </div>
        </div>

        {/* Chart card */}
        <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-[#0e1d42]/60 to-[#0b1738]/60 backdrop-blur-md p-6 sm:p-8 mb-6" data-testid="insights-chart-card">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
            <div>
              <div className="font-heading text-white text-xl sm:text-2xl leading-tight">The trend</div>
              <div className="text-zinc-400 text-sm mt-0.5">{range === "3M" ? "Last 3 months" : range === "12M" ? "Last 12 months" : "Last 6 months"} — how it's moved</div>
            </div>
            <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.02] p-1" data-testid="insights-range">
              {["3M", "6M", "12M"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRange(r)}
                  className={`px-3.5 py-1.5 rounded-full text-[12px] font-heading font-medium tracking-wide transition ${range === r ? "bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] shadow-[0_4px_14px_rgba(238,216,154,0.25)]" : "text-zinc-300 hover:text-white"}`}
                  data-testid={`insights-range-${r}`}
                >{r}</button>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 text-[11.5px] text-zinc-300/90 mb-3">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1 rounded-full bg-[#7de3c5]" /> Vibe</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1 rounded-full bg-[#EED89A]" /> Energy</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1 rounded-full bg-[#6ea3ff]" /> Focus</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1 rounded-full bg-[#c5b0ff]" /> Rest</span>
          </div>

          {/* Chart */}
          <div className="relative w-full">
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" className="block" aria-label="Vibe / Energy / Focus / Rest over time">
              <defs>
                <linearGradient id="insights-vibe-fill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%"  stopColor="#7de3c5" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="#7de3c5" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Grid */}
              {[0, 25, 50, 75, 100].map((g) => {
                const y = 12 + (1 - g / 100) * (H - 24);
                return <line key={g} x1="12" y1={y} x2={W - 12} y2={y} stroke="rgba(255,255,255,0.06)" strokeDasharray="2 4" />;
              })}
              {/* Vibe area fill */}
              <path d={`${vibe.d} L ${W - 12} ${H - 12} L 12 ${H - 12} Z`} fill="url(#insights-vibe-fill)" />
              {/* Lines */}
              <path d={rest.d}   fill="none" stroke="#c5b0ff" strokeWidth="2.2" strokeLinecap="round" opacity="0.85" />
              <path d={focus.d}  fill="none" stroke="#6ea3ff" strokeWidth="2.4" strokeLinecap="round" opacity="0.9" />
              <path d={energy.d} fill="none" stroke="#EED89A" strokeWidth="2.4" strokeLinecap="round" opacity="0.9" />
              <path d={vibe.d}   fill="none" stroke="#7de3c5" strokeWidth="3"   strokeLinecap="round" />
              {/* Endpoint dots */}
              {[["#c5b0ff", rest.pts], ["#6ea3ff", focus.pts], ["#EED89A", energy.pts], ["#7de3c5", vibe.pts]].map(([c, pts], i) => {
                const [x, y] = pts[pts.length - 1];
                return <circle key={i} cx={x} cy={y} r={i === 3 ? 5 : 3.6} fill={c} stroke="#0e1d42" strokeWidth="1.5" />;
              })}
              {/* Scrubber */}
              {hover !== null && (() => {
                const [hx] = vibe.pts[hover]; const [, hy] = vibe.pts[hover];
                return (
                  <g>
                    <line x1={hx} y1={12} x2={hx} y2={H - 20} stroke="rgba(125,227,197,0.5)" strokeWidth="1" strokeDasharray="2 3" />
                    <circle cx={hx} cy={hy} r="6" fill="#7de3c5" stroke="#fff" strokeWidth="1.2" />
                  </g>
                );
              })()}
              {/* Hitboxes + x labels */}
              {data.labels.map((lbl, i) => {
                const bx = 12 + (i / (data.labels.length - 1)) * (W - 24);
                const bw = (W - 24) / Math.max(1, data.labels.length - 1);
                return (
                  <g key={lbl}>
                    <rect
                      x={bx - bw / 2} y={0} width={bw} height={H - 20}
                      fill="transparent" style={{ cursor: "crosshair" }}
                      onMouseEnter={() => setHover(i)}
                      onMouseLeave={() => setHover((v) => (v === i ? null : v))}
                    />
                    <text x={bx} y={H - 4} fill={i === hover ? "rgba(125,227,197,0.95)" : "rgba(255,255,255,0.5)"} fontSize="10" textAnchor="middle" fontFamily="var(--font-body)">{lbl}</text>
                  </g>
                );
              })}
            </svg>
            {/* Readout */}
            {hover !== null && (
              <div className="absolute top-2 right-2 rounded-xl border border-emerald-300/25 bg-[#0a1530]/85 backdrop-blur px-3 py-2 text-[11.5px] pointer-events-none">
                <div className="text-emerald-200/80 font-heading tracking-wider uppercase text-[9.5px] mb-1">{data.labels[hover]}</div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#7de3c5]" /> Vibe · <span className="text-white font-medium">{data.vibe[hover]}</span></div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#EED89A]" /> Energy · <span className="text-white font-medium">{data.energy[hover]}</span></div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#6ea3ff]" /> Focus · <span className="text-white font-medium">{data.focus[hover]}</span></div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#c5b0ff]" /> Rest · <span className="text-white font-medium">{data.rest[hover]}</span></div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom row — Themes + Moments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Themes */}
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6" data-testid="insights-themes">
            <div className="text-[11px] uppercase tracking-[0.24em] text-emerald-300/85 mb-4">Recurring themes</div>
            <div className="space-y-3">
              {THEMES.map((t) => (
                <div key={t.label} className="flex items-center gap-3">
                  <div className="text-[13.5px] text-white font-heading w-20 flex-shrink-0">{t.label}</div>
                  <div className="flex-1 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${t.value}%`, background: t.color, boxShadow: `0 0 12px ${t.color}55`, transition: "width 0.8s ease" }}
                    />
                  </div>
                  <div className="text-[12px] text-zinc-400 w-8 text-right">{t.value}</div>
                </div>
              ))}
            </div>
            <p className="mt-5 text-[12px] text-zinc-500 italic leading-relaxed">
              Themes that show up together across your reflections — bars grow as the pattern deepens.
            </p>
          </div>

          {/* Moments */}
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6" data-testid="insights-moments">
            <div className="text-[11px] uppercase tracking-[0.24em] text-amber-300/85 mb-4">Key moments</div>
            <div className="space-y-3">
              {MOMENTS.map((m) => (
                <div key={m.title} className="flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.02] px-3.5 py-2.5">
                  <span className="text-[18px] leading-none" aria-hidden>{m.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-white font-heading text-[13.5px] leading-tight">{m.title}</div>
                      <span className="text-[9.5px] uppercase tracking-[0.2em] text-amber-200/60">{m.tag}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{m.date}</div>
                  </div>
                  <div className="text-emerald-300/95 font-heading text-[13px] flex-shrink-0">{m.vibe}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-4 text-zinc-500 text-xs">
          <div className="italic">A demonstration of Mood Insights. Live data comes from your grooves once you begin.</div>
          <div>© {new Date().getFullYear()} My Mood Groove</div>
        </div>
      </main>
    </div>
  );
}
